import React, { useState, useRef } from 'react';
import { adminAPI } from '@/lib/api';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload, Loader2, FileText, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';

export const API_URL = process.env.REACT_APP_BACKEND_URL;
export const FAVICON_URL = "https://customer-assets.emergentagent.com/job_multi-watt-hub/artifacts/q9v3nz6d_favicon-riding-watt.png";

export const statusColors = {
  confirmed: 'bg-[#00FF00]/15 text-[#00FF00]',
  pending: 'bg-yellow-500/15 text-yellow-400',
  pending_onsite: 'bg-blue-500/15 text-blue-400',
  pending_payment_link: 'bg-purple-500/15 text-purple-400',
  cancelled: 'bg-red-500/15 text-red-400',
};

export const EMPTY_ACTIVITY = { site_id: '', name: '', type: '', description: '', image_url: '', durations: [{ duration_minutes: 30, label: '30 minutes', price: 0 }], active: true, max_slots: 1, resource_id: '', contract_url: '', available_from: '', available_to: '' };

export const DURATION_PRESETS = [
  { minutes: 30, label: '30 minutes' }, { minutes: 60, label: '1 heure' },
  { minutes: 240, label: 'Demi-journée' }, { minutes: 480, label: 'Journée' },
];

export const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/uploads')) return `${API_URL}${url}`;
  if (url.startsWith('/uploads')) return `${API_URL}/api${url}`;
  return url;
};

export const compressImage = (file, maxWidth = 1600, quality = 0.8) => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(file); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width <= maxWidth && file.size <= 500 * 1024) { resolve(file); return; }
      if (width > maxWidth) { height = Math.round(height * maxWidth / width); width = maxWidth; }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) { resolve(file); return; }
        resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
      }, 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
};

export const ImageUploader = ({ currentUrl, onUpload, label }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputId = useRef(`img-upload-${Math.random().toString(36).slice(2)}`).current;
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Veuillez sélectionner une image'); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error('Image trop lourde (max 50MB)'); return; }
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    try {
      setProgress('Compression...');
      const compressed = await compressImage(file);
      const sizeKB = (compressed.size / 1024).toFixed(0);
      setProgress(`Upload ${sizeKB}KB...`);
      const res = await adminAPI.uploadBase64(compressed);
      onUpload(res.data.url);
      toast.success('Image uploadée !');
      setProgress('');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      toast.error(`Échec: ${detail || err.message || 'Erreur réseau'}`);
      setPreviewUrl(null);
      setProgress('');
    }
    setUploading(false);
    e.target.value = '';
  };
  const displaySrc = previewUrl || resolveImageUrl(currentUrl);
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-300 flex items-center gap-1 mb-2"><ImageIcon className="w-3 h-3" /> {label || 'Photo'}</Label>
      {displaySrc && <img src={displaySrc} alt="" className="w-full h-28 object-cover rounded-lg border border-[#2A2A2A] mb-2" onError={(e) => { e.target.style.display = 'none'; }} />}
      <input id={inputId} type="file" accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif" className="sr-only" onChange={handleFile} disabled={uploading} />
      <label htmlFor={inputId} className={`border-2 border-dashed border-[#2A2A2A] hover:border-[#00FF00]/50 rounded-lg p-4 text-center cursor-pointer transition-colors bg-[#0A0A0A] block ${uploading ? 'pointer-events-none opacity-60' : ''}`} data-testid="image-upload-zone">
        {uploading ? (<><Loader2 className="w-5 h-5 mx-auto text-[#00FF00] animate-spin" />{progress && <p className="text-xs text-[#00FF00] mt-1">{progress}</p>}</>) : (<><Upload className="w-5 h-5 mx-auto text-slate-500 mb-1" /><p className="text-xs text-slate-500">Cliquez pour uploader</p><p className="text-[10px] text-slate-700 mt-0.5">JPG, PNG, WEBP (max 25MB)</p></>)}
      </label>
    </div>
  );
};

export const ContractUploader = ({ currentUrl, onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const inputId = useRef(`pdf-${Math.random().toString(36).slice(2)}`).current;
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Format PDF uniquement'); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error('Max 25MB'); return; }
    setUploading(true);
    try {
      const res = await adminAPI.uploadPdf(file);
      onUpload(res.data.url);
      toast.success('Contrat uploadé !');
    } catch (err) {
      toast.error('Échec upload PDF');
    }
    setUploading(false);
    e.target.value = '';
  };
  const contractSrc = resolveImageUrl(currentUrl);
  const contractName = currentUrl ? currentUrl.split('/').pop() : null;
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-300 flex items-center gap-1 mb-2"><FileText className="w-3 h-3" /> Contrat de location (PDF)</Label>
      {contractName && (
        <div className="flex items-center gap-2 mb-2 bg-[#1A1A1A] rounded-lg p-3 border border-[#2A2A2A]">
          <FileText className="w-4 h-4 text-[#00FF00] flex-shrink-0" />
          <span className="text-xs text-slate-300 truncate flex-1">{contractName}</span>
          <a href={contractSrc} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00FF00] hover:underline flex-shrink-0">Voir</a>
          <button onClick={() => onUpload('')} className="text-xs text-red-400 hover:text-red-300 flex-shrink-0">Retirer</button>
        </div>
      )}
      <input id={inputId} type="file" accept=".pdf,application/pdf" className="sr-only" onChange={handleFile} disabled={uploading} />
      <label htmlFor={inputId} className={`border-2 border-dashed border-[#2A2A2A] hover:border-[#00FF00]/50 rounded-lg p-3 text-center cursor-pointer transition-colors bg-[#0A0A0A] block ${uploading ? 'pointer-events-none opacity-60' : ''}`} data-testid="contract-upload-zone">
        {uploading ? <Loader2 className="w-5 h-5 mx-auto text-[#00FF00] animate-spin" /> : (<><Upload className="w-4 h-4 mx-auto text-slate-500 mb-1" /><p className="text-xs text-slate-500">{currentUrl ? 'Remplacer le PDF' : 'Uploader un contrat PDF'}</p></>)}
      </label>
    </div>
  );
};
